import { computed, type ComputedRef } from 'vue';

import { isMeaningfulString, isNonNullable } from '@/shared/utils/is';
import { useStorage } from '@vueuse/core';

// Auth localStorage contract. No `x-sr-user`: user info is fetched live via
// /api/user-info, so v3 must not invent a persisted user-info key.
//
//   x-sr-token    : auth token returned by /api/login
//   x-sr-ubid     : user browser id
//   x-sr-aid      : current account id
//   x-sr-account  : JSON array of `${accountName}_${aid}` strings (recent
//                   accounts list, rendered as the choose-account screen)
//
// token + ubid are the pair the backend signs against; aid rides along when
// present but is not required to authenticate a request, and it survives
// logout so the re-login screen can prefill the account. Account list has its
// own append-on-login / delete-on-close-icon semantics.

export type StoredAuth = {
  token: string;
  ubid: string;
  aid: string;
};

export const STORAGE_KEYS = {
  token: 'x-sr-token',
  ubid: 'x-sr-ubid',
  aid: 'x-sr-aid',
  account: 'x-sr-account',
  // Removed on logout for cross-version cleanup. Never written by v3.
  user: 'x-sr-user',
} as const;

export const tokenStorage = {
  read(): StoredAuth | null {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    const ubid = localStorage.getItem(STORAGE_KEYS.ubid);
    if (!token || !ubid) return null;
    return { token, ubid, aid: localStorage.getItem(STORAGE_KEYS.aid) ?? '' };
  },

  write(snapshot: StoredAuth): void {
    localStorage.setItem(STORAGE_KEYS.token, snapshot.token);
    localStorage.setItem(STORAGE_KEYS.ubid, snapshot.ubid);
    // Persist aid only when it's a real value — never the literal 'undefined',
    // which would poison the re-login redirect (mirrors legacy saveUbidToken).
    if (isMeaningfulString(snapshot.aid)) localStorage.setItem(STORAGE_KEYS.aid, snapshot.aid);
  },

  clear(): void {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.ubid);
    // aid is intentionally kept: the re-login redirect reads it to prefill the
    // account (mirrors legacy exitToLoginPage, which never removes it).
    // Defensive cleanup for old sessions that might still carry this key.
    localStorage.removeItem(STORAGE_KEYS.user);
  },
};

// On-disk shape: JSON array of `"${name}${SEP}${aid}"` strings.  Name itself
// can contain the separator, so split-from-end is required when parsing back.

export type StoredAccount = {
  id: string;
  name: string;
};

// Codec for the `"name_id"` storage shape. Centralizes the separator so the
// rest of the file works in terms of `StoredAccount`, never raw strings.
const accountCodec = {
  separator: '_',
  encode(name: string, id: string): string {
    return `${name}${this.separator}${id}`;
  },
  decode(raw: string): StoredAccount | null {
    const parts = raw.split(this.separator);
    if (parts.length < 2) return null;
    const id = parts[parts.length - 1];
    const name = parts.slice(0, -1).join(this.separator);
    // Scrub stale corrupt entries ("undefined_undefined", empty halves) —
    // rendering them strands the user on auth requests with
    // accountID='undefined'.
    if (!isMeaningfulString(id) || !isMeaningfulString(name)) return null;
    return { id, name };
  },
  decodeId(raw: string): string | null {
    return this.decode(raw)?.id ?? null;
  },
};

// On-disk shape (string[] of encoded entries) is shared across host/sub during
// the wujie window. useStorage gives us reactivity + cross-tab sync for free;
// the computed view layers the StoredAccount shape on top.
const rawAccounts = useStorage<string[]>(STORAGE_KEYS.account, []);

export const accountList: ComputedRef<StoredAccount[]> = computed(() => {
  const seen = new Set<string>();
  return rawAccounts.value
    .map((raw) => accountCodec.decode(raw))
    .filter(isNonNullable)
    .filter((v) => {
      if (seen.has(v.id)) return false;
      seen.add(v.id);
      return true;
    });
});

// Append only if aid is not already present. Empty name or aid is a no-op.
export function addAccount(name: string | undefined, aid: string | undefined): void {
  if (!name || !aid) return;
  const existingIds = rawAccounts.value.map((entry) => accountCodec.decodeId(entry));
  if (existingIds.includes(aid)) return;
  rawAccounts.value = [...rawAccounts.value, accountCodec.encode(name, aid)];
}

// Remove matching aid; when the list goes empty, remove the key entirely so a
// stale `"[]"` value doesn't linger in storage.
export function removeAccount(aid: string): void {
  const next = rawAccounts.value.filter((entry) => accountCodec.decodeId(entry) !== aid);
  if (next.length === 0) {
    rawAccounts.value = [];
    localStorage.removeItem(STORAGE_KEYS.account);
    return;
  }
  rawAccounts.value = next;
}

// Refresh the display names of already-stored accounts (the recent-account
// switcher) from a fresh account list — updates in place by id when the name
// changed, never adds new entries. Mirrors legacy syncStoredAccountNames so the
// switcher tracks a rename done on the organization page.
export function syncStoredAccountNames(
  accounts: { account_id?: string; account_name?: string }[],
): void {
  if (!accounts.length || !rawAccounts.value.length) return;
  let changed = false;
  const next = rawAccounts.value.map((raw) => {
    const entry = accountCodec.decode(raw);
    if (!entry) return raw;
    const match = accounts.find((a) => a.account_id === entry.id);
    if (match?.account_name && match.account_name !== entry.name) {
      changed = true;
      return accountCodec.encode(match.account_name, entry.id);
    }
    return raw;
  });
  if (changed) rawAccounts.value = next;
}

// Test-only — rawAccounts is a module-level useStorage ref, so clearing
// localStorage between tests doesn't empty the in-memory list.
export function __resetAccountsForTests(): void {
  rawAccounts.value = [];
}
